import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useRef, useState } from 'react';

import type { CustomSkillRecord } from '../../../../../queries/chats';
import {
  GET_CUSTOM_SKILL_GROUP_SHARES,
  SHARE_SETTINGS_CUSTOM_SKILL_WITH_GROUP,
  UNSHARE_SETTINGS_CUSTOM_SKILL_FROM_GROUP,
  type SkillShareGroup,
} from '../../../../../queries/skill-group-sharing';
import { getErrorMessage } from '../../../lib/error-message';
import { updateCachedSkillGroupAccess } from '../../../lib/skill-group-cache';
import { useNovaContext } from '../../../providers';
import { useSettingsRemoval } from '../../settings-shared/use-settings-removal';
import { getRootCustomSkillUuid } from '../custom-skill-model';

export interface ShareGroupsResult {
  failedGroupUuids: string[];
  error?: string;
}

function requireGroupMutationResult(
  response: { data?: { group: { uuid: string } } | null; errors?: readonly { message: string }[] },
  groupId: string,
) {
  if (response.errors?.length) throw new Error(response.errors.map((error) => error.message).join('\n'));
  if (response.data?.group.uuid !== groupId) throw new Error('The sharing change was not confirmed.');
}

export function useCustomSkillGroupShares(skill: CustomSkillRecord, onChanged: () => Promise<void>) {
  const client = useApolloClient();
  const { userUuid } = useNovaContext();
  const notify = useNotify();
  const skillUuid = getRootCustomSkillUuid(skill);
  const variables = { skillUuid };
  const { data, loading, error, refetch } = useQuery(GET_CUSTOM_SKILL_GROUP_SHARES, {
    variables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });
  const [shareGroup] = useMutation(SHARE_SETTINGS_CUSTOM_SKILL_WITH_GROUP, { fetchPolicy: 'no-cache' });
  const [unshareGroup] = useMutation(UNSHARE_SETTINGS_CUSTOM_SKILL_FROM_GROUP, { fetchPolicy: 'no-cache' });
  const pending = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const removal = useSettingsRemoval(skillUuid, !isSaving, userUuid);

  const refreshAfterChange = async () => {
    try {
      await onChanged();
    } catch {
      notify.error('Sharing was saved, but skill details could not refresh.');
    }
  };

  const addGroups = async (groups: SkillShareGroup[]): Promise<ShareGroupsResult> => {
    if (pending.current || removal.pendingKey) return { failedGroupUuids: groups.map((group) => group.uuid) };
    pending.current = true;
    setIsSaving(true);
    try {
      const results = await Promise.allSettled(
        groups.map(async (group) => {
          const response = await shareGroup({ variables: { skillUuid, groupId: group.uuid } });
          requireGroupMutationResult(response, group.uuid);
          // Publish each confirmed share immediately. Discovery returns a different GraphQL group type.
          updateCachedSkillGroupAccess(client.cache, { kind: 'share', group, skillUuid });
          return group;
        }),
      );
      const successful = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      const failed = results.flatMap((result, index) =>
        result.status === 'rejected'
          ? [{ group: groups[index], message: getErrorMessage(result.reason, 'Unable to share this skill.') }]
          : [],
      );
      if (successful.length) {
        // A lagging assignments read must not overwrite confirmed mutations.
        void refreshAfterChange();
      }
      if (!failed.length) notify.success('Custom skill shared.');
      return {
        failedGroupUuids: failed.map(({ group }) => group.uuid),
        error: failed.length ? failed.map(({ group, message }) => `${group.name}: ${message}`).join('\n') : undefined,
      };
    } finally {
      pending.current = false;
      setIsSaving(false);
    }
  };

  const removeGroup = (group: SkillShareGroup) => {
    if (pending.current) return Promise.resolve(false);
    return removal.run(
      group.uuid,
      async () => {
        const response = await unshareGroup({ variables: { skillUuid, groupId: group.uuid } });
        requireGroupMutationResult(response, group.uuid);
      },
      () => {
        updateCachedSkillGroupAccess(client.cache, { kind: 'revoke', groupId: group.uuid, skillUuid });
        void refreshAfterChange();
      },
      `Skill access removed from ${group.name}.`,
    );
  };

  return {
    groups: data?.getCustomSkillGroupAssignments.groups ?? [],
    isInitialLoading: loading && !data,
    isSaving: isSaving || Boolean(removal.pendingKey),
    removingGroupUuid: removal.pendingKey,
    removalError: removal.error,
    clearRemovalError: removal.clearError,
    error,
    reload: () => refetch(),
    addGroups,
    removeGroup,
  };
}
